import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Grid,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  Button,
  Popover,
} from "@mui/material";
import FlexBetween from "../../FlexBetween";

import {
  getDateFromTimestamp,
  getFormattedTime,
  formatNumberWithCommas,
} from "../../../services/utils";
import { Link, useNavigate } from "react-router-dom";
import { IconReRunWhite, IconDbInfo } from "../../../common/utils/icons";
import { useAuth } from "../../../contexts/AuthContext";
import { IValidationResultHeader } from "../../../types/Validation.type";
import ScoreBadge from "../../Scoring/ScoreBadge";
import ScoreDetailsDrawer from "../../Scoring/ScoreDetailsDrawer";
import { getValidationExecutionHistory } from "../../../services/scoringService";
import {
  getExecutionSubGroupName,
  getExecutionSubGroupRef,
} from "../../../utils/executionGroup";
import useExecutionSubGroupMap from "../../../hooks/useExecutionSubGroupMap";
import { buildReRunValidateResourceUrl } from "../../../utils/rerunQueryParams";

const ValidationResultHeader = ({
  validateResult,
}: IValidationResultHeader) => {
  const [validationScore, setValidationScore] = useState<any>(null);
  const [scoreDrawerOpen, setScoreDrawerOpen] = useState(false);
  const executionSubGroupMap = useExecutionSubGroupMap();
  const executionSubGroupRef = getExecutionSubGroupRef(validateResult);
  const executionSubGroupName = getExecutionSubGroupName(
    executionSubGroupRef,
    executionSubGroupMap,
  );

  // Lightweight header-only fetch: pulls the latest score via the paginated
  // history endpoint (page=1, size=10 — backend minimum) and takes items[0].
  // Replaces the redundant /scores/validation/{id} request. Full breakdown
  // and history are fetched on-demand when the Drawer is opened.
  useEffect(() => {
    const execId = validateResult?.id;
    if (!execId || execId <= 0) {
      setValidationScore(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await getValidationExecutionHistory({
          executionId: execId,
          page: 1,
          pSize: 10,
        });
        if (!cancelled) setValidationScore(res?.items?.[0] || null);
      } catch {
        if (!cancelled) setValidationScore(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [validateResult?.id]);
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canResourceValidation = hasPermission("resource", "validate");
  const baseResourceId =
    validateResult?.additional_properties?.base_resource_validation_result;

  // DB Info Popover state management
  const [dbInfoAnchorEl, setDbInfoAnchorEl] = useState<HTMLElement | null>(
    null,
  );

  const handleDbInfoClick = (event: React.MouseEvent<HTMLElement>) => {
    setDbInfoAnchorEl(event.currentTarget);
  };

  const handleDbInfoClose = () => {
    setDbInfoAnchorEl(null);
  };

  const isDbInfoPopoverOpen = Boolean(dbInfoAnchorEl);

  return (
    <div
      className={`header-group ${
        baseResourceId != null ? "has-base-resource" : ""
      }`}
    >
      <Stack
        direction="row"
        spacing={2.5}
        alignItems="center"
        flexWrap="wrap"
        divider={
          <Divider
            orientation="vertical"
            flexItem
            sx={{ mx: 0.5, borderColor: "divider" }}
          />
        }
        sx={{ width: "100%", rowGap: 1.5 }}
      >
        {/* CLUSTER 1 — Identity + Context */}
        <Box
          className="heading execution-name-heading"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.25,
            flex: "1 1 auto",
            minWidth: 0,
          }}
        >
          <Tooltip
            title={
              validateResult?.complete_validation_params
                ?.validation_request_body?.execution_name || ""
            }
            placement="top"
            arrow
          >
            <Typography
              variant="h6"
              noWrap
              sx={{ fontWeight: 700, lineHeight: 1.3 }}
            >
              {
                validateResult?.complete_validation_params
                  ?.validation_request_body?.execution_name
              }
            </Typography>
          </Tooltip>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            divider={
              <Box
                component="span"
                sx={{ color: "text.disabled", lineHeight: 1 }}
              >
                ·
              </Box>
            }
            sx={{ color: "text.secondary", rowGap: 0.25 }}
          >
            {(validateResult?.run_name || validateResult?.run_id) && (
              <Typography variant="body2" color="inherit">
                Execution Group: {validateResult?.run_name ?? "—"}
                {validateResult?.run_id ? ` (${validateResult?.run_id})` : ""}
              </Typography>
            )}
            {executionSubGroupName && (
              <Typography variant="body2" color="inherit">
                Execution Sub Group: {executionSubGroupName}
              </Typography>
            )}
            {validateResult?.execution_time && (
              <Typography variant="body2" color="inherit">
                {getDateFromTimestamp(validateResult?.execution_time)}{" "}
                {getFormattedTime(validateResult?.execution_time)}
              </Typography>
            )}
          </Stack>
        </Box>
        {/* DB Info Icon with Popover */}
        {validateResult &&
          validateResult?.additional_properties &&
          !validateResult?.additional_properties?.are_all_errors_stored && (
            <div className="popover-box">
              <div
                className="db-info-icon"
                onClick={handleDbInfoClick}
                style={{}}
              >
                <IconDbInfo />
              </div>
              <Popover
                open={isDbInfoPopoverOpen}
                anchorEl={dbInfoAnchorEl}
                onClose={handleDbInfoClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                slotProps={{
                  paper: {
                    className: "db-info-popover",
                  },
                }}
              >
                <Box className="db-error-popover-body">
                  <div className="title">Records summary</div>

                  <ul>
                    <li>
                      <div className="item-parent">
                        <div className="item1">
                          Filtered records by null values :{" "}
                        </div>
                        <div className="item2">
                          Generated &nbsp; - &nbsp;
                          {formatNumberWithCommas(
                            validateResult?.additional_properties
                              ?.filtered_records_by_null_values_in_unique_keys ||
                              0,
                          )}
                        </div>

                        <div className="item3">
                          Recorded &nbsp; - &nbsp;
                          {formatNumberWithCommas(
                            validateResult?.additional_properties
                              ?.filtered_records_by_null_values_in_unique_keys_stored_in_db ||
                              0,
                          )}
                        </div>
                      </div>
                    </li>
                    <li>
                      <div className="item-parent">
                        <div className="item1">Total adhoc query results :</div>
                        <div className="item2">
                          Generated &nbsp; - &nbsp;
                          {formatNumberWithCommas(
                            validateResult?.additional_properties
                              ?.total_cross_field_validation_results || 0,
                          )}
                        </div>
                        <div className="item3">
                          Recorded &nbsp; - &nbsp;
                          {formatNumberWithCommas(
                            validateResult?.additional_properties
                              ?.total_cross_field_validation_results_stored_in_db ||
                              0,
                          )}
                        </div>
                      </div>
                    </li>
                    <li>
                      <div className="item-parent">
                        <div className="item1">Total duplicate records :</div>
                        <div className="item2">
                          Generated &nbsp; - &nbsp;
                          {formatNumberWithCommas(
                            validateResult?.additional_properties
                              ?.total_duplicate_records || 0,
                          )}
                        </div>
                        <div className="item3">
                          Recorded &nbsp; - &nbsp;
                          {formatNumberWithCommas(
                            validateResult?.additional_properties
                              ?.total_duplicate_records_stored_in_db || 0,
                          )}
                        </div>
                      </div>
                    </li>
                    <li>
                      <div className="item-parent">
                        <div className="item1">Total validation errors :</div>
                        <div className="item2">
                          Generated &nbsp; - &nbsp;
                          {formatNumberWithCommas(
                            validateResult?.additional_properties
                              ?.total_validation_errors || 0,
                          )}
                        </div>
                        <div className="item3">
                          Recorded &nbsp; - &nbsp;
                          {formatNumberWithCommas(
                            validateResult?.additional_properties
                              ?.total_validation_errors_stored_in_db || 0,
                          )}
                        </div>
                      </div>
                    </li>
                  </ul>
                </Box>
              </Popover>
            </div>
          )}

        {/* CLUSTER 3 — Result (Status + Score) */}
        {(validateResult?.job_status === "completed" ||
          validateResult?.job_status === "completed_with_errors" ||
          validationScore ||
          validateResult?.additional_properties?.summary_mode) && (
          <Stack direction="row" spacing={1} alignItems="center">
            {validateResult?.job_status === "completed" && (
              <label className="badges badge-base badge-success">
                Completed
              </label>
            )}
            {validateResult?.job_status === "completed_with_errors" && (
              <label className="badges badge-base badge-error">
                Completed With Errors
              </label>
            )}
            {validateResult?.additional_properties?.summary_mode && (
              <label className="badges badge-base badge-blue">
                Summary Mode
              </label>
            )}
            {validationScore && (
              <ScoreBadge
                score={validationScore.score}
                size="medium"
                onClick={() => setScoreDrawerOpen(true)}
                tooltipText="Click to view score details"
              />
            )}
          </Stack>
        )}

        {/* CLUSTER 4 — Actions (Base Resource link + Re-run) */}
        {(baseResourceId != null || canResourceValidation) && (
          <Stack direction="row" spacing={1.5} alignItems="center">
            {baseResourceId != null && (
              <Button
                variant="contained"
                className="btn-parent base-resource-validation-result"
              >
                <a
                  href={`/validation-execution-history/validate-result/${baseResourceId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Base Resource Validation Result
                </a>
              </Button>
            )}
            {canResourceValidation && (
              <Button
                className="btn-orange btn-dark btn-sm plus-btn-sm"
                onClick={() =>
                  navigate(
                    buildReRunValidateResourceUrl({
                      domainId: validateResult?.domain_id,
                      resourceId: validateResult?.resource_id,
                      validationId: validateResult?.id,
                      runId: validateResult?.run_id,
                      runName: validateResult?.run_name,
                      executionSubGroupId:
                        executionSubGroupRef.execution_sub_group_id,
                      executionSubGroupCode:
                        executionSubGroupRef.execution_sub_group_code,
                    }),
                  )
                }
              >
                <IconReRunWhite /> &nbsp; ReRun
              </Button>
            )}
          </Stack>
        )}
      </Stack>
      <ScoreDetailsDrawer
        open={scoreDrawerOpen}
        onClose={() => setScoreDrawerOpen(false)}
        type="validation"
        executionId={validateResult?.id}
      />
    </div>
  );
};

export default ValidationResultHeader;
